import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

const TiltedCard = ({
  imageSrc,
  altText,
  captionText,
  containerHeight = '300px',
  containerWidth = '300px',
  imageHeight = '300px',
  imageWidth = '300px',
  rotateAmplitude = 12,
  scaleOnHover = 1.2,
  showMobileWarning = false,
  showTooltip = true,
  displayOverlayContent = true,
  overlayContent,
  className = '',
}) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const cardRef = useRef(null);
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Calculate rotation (inverted, so the card "follows" the cursor)
    const rotateX = -y / (rect.height / 2) * rotateAmplitude;
    const rotateY = x / (rect.width / 2) * rotateAmplitude;
    
    setRotation({ x: rotateX, y: rotateY });
  };
  
  const handleMouseEnter = () => {
    setIsHovered(true);
    setTooltipVisible(true);
    
    // Hide tooltip after 2 seconds
    if (showTooltip) {
      setTimeout(() => {
        setTooltipVisible(false);
      }, 2000);
    }
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    setRotation({ x: 0, y: 0 });
    setTooltipVisible(false);
  };
  
  return (
    <div
      className={`relative overflow-visible ${className}`}
      style={{
        height: containerHeight,
        width: containerWidth,
        perspective: '1000px',
      }}
    >
      {showMobileWarning && (
        <div className="md:hidden absolute -top-8 left-0 right-0 text-center text-xs text-gray-500">
          Tilt effect works best on desktop devices
        </div>
      )}
      
      <motion.div
        ref={cardRef}
        className="relative w-full h-full overflow-hidden rounded-lg"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        animate={{
          rotateX: rotation.x,
          rotateY: rotation.y,
          scale: isHovered ? scaleOnHover : 1,
          zIndex: isHovered ? 20 : 10,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 15,
          mass: 0.8,
        }}
        style={{
          transformStyle: 'preserve-3d',
          touchAction: 'none',
        }}
      >
        {/* Card Image */}
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center rounded-lg"
          style={{
            backgroundImage: `url(${imageSrc})`,
            width: imageWidth,
            height: imageHeight,
            transformStyle: 'preserve-3d',
            transform: 'translateZ(0px)',
          }}
        />
        
        {/* Overlay Gradient */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-lg"
          style={{
            transformStyle: 'preserve-3d',
            transform: 'translateZ(2px)',
          }}
        />
        
        {/* Caption Text */}
        {captionText && !displayOverlayContent && (
          <div
            className="absolute bottom-4 left-4 text-white font-bold"
            style={{
              transformStyle: 'preserve-3d',
              transform: 'translateZ(5px)',
            }}
          >
            {captionText}
          </div>
        )}
        
        {/* Custom Overlay Content */}
        {displayOverlayContent && overlayContent && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transformStyle: 'preserve-3d',
              transform: 'translateZ(5px)',
            }}
          >
            {overlayContent}
          </div>
        )}
        
        {/* Tooltip */}
        {showTooltip && tooltipVisible && (
          <motion.div
            className="absolute top-2 left-0 right-0 mx-auto text-white text-xs text-center bg-black/70 py-1 px-2 rounded-full w-max"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              transformStyle: 'preserve-3d',
              transform: 'translateZ(10px)',
            }}
          >
            Move mouse to tilt
          </motion.div>
        )}
        
        {/* Shimmering Effect */}
        <div
          className="absolute inset-0 opacity-0 bg-gradient-to-tr from-transparent via-white to-transparent rounded-lg"
          style={{
            transformStyle: 'preserve-3d',
            transform: 'translateZ(3px) rotate(25deg)',
            backgroundSize: '200% 200%',
            opacity: isHovered ? 0.1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        />
      </motion.div>
    </div>
  );
};

export default TiltedCard; 